class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.trackCartRenderIntent();
    this.setHeaderCartIconAccessibility();
    this.observeDrawerHeader();
  }

  isDebugEnabled() {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('cart_debug') === '1') {
        window.localStorage?.setItem('PUPFORD_CART_DEBUG', '1');
      } else if (params.get('cart_debug') === '0') {
        window.localStorage?.removeItem('PUPFORD_CART_DEBUG');
      }
      return window.localStorage?.getItem('PUPFORD_CART_DEBUG') === '1';
    } catch (_) {
      return false;
    }
  }

  debug(label, detail = {}) {
    if (!this.isDebugEnabled()) return;
    const event = {
      label,
      t: Math.round(performance.now()),
      url: window.location.href,
      drawerActive: this.classList.contains('active'),
      activeElement: document.activeElement
        ? `${document.activeElement.tagName.toLowerCase()}${document.activeElement.id ? `#${document.activeElement.id}` : ''}${document.activeElement.className ? `.${String(document.activeElement.className).trim().replace(/\s+/g, '.')}` : ''}`
        : '',
      ...detail,
    };
    window.__pupfordCartDebugEvents = window.__pupfordCartDebugEvents || [];
    window.__pupfordCartDebugEvents.push(event);
    if (window.__pupfordCartDebugEvents.length > 200) window.__pupfordCartDebugEvents.shift();
    console.log('[Pupford cart debug]', label, event);
  }

  summarizeCartPayload(parsedState) {
    if (!parsedState || typeof parsedState !== 'object') {
      return { type: typeof parsedState };
    }
    return {
      hasId: Boolean(parsedState.id),
      hasKey: Boolean(parsedState.key),
      hasSections: Boolean(parsedState.sections),
      sectionKeys: parsedState.sections ? Object.keys(parsedState.sections) : [],
      hasItemsArray: Array.isArray(parsedState.items),
      itemsLength: Array.isArray(parsedState.items) ? parsedState.items.length : undefined,
      itemCount: parsedState.item_count,
      totalPrice: parsedState.total_price,
    };
  }

  trackCartRenderIntent() {
    if (window.__pupfordCartRenderIntentTrackingInstalled) return;
    window.__pupfordCartRenderIntentTrackingInstalled = true;
    window.__pupfordLastTrustedCartRenderIntent = 0;

    const markIntent = (event) => {
      if (!event?.isTrusted) return;
      const target = event.target;
      if (!(target instanceof Element)) return;

      if (
        target.closest('product-form form') ||
        target.closest('[data-offer-v2-form]') ||
        target.closest('[data-offer-cta] button[type="submit"]') ||
        target.closest('.button-add-to-cart') ||
        target.closest('.pupford-cart-upsell__cta')
      ) {
        window.__pupfordLastTrustedCartRenderIntent = Date.now();
        this.debug('cart-render-intent', {
          eventType: event.type,
          target: target.outerHTML ? target.outerHTML.slice(0, 240) : target.tagName,
        });
      }
    };

    window.addEventListener('pointerdown', markIntent, true);
    window.addEventListener('click', markIntent, true);
    window.addEventListener('submit', markIntent, true);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    this.debug('cart-drawer.open', {
      triggeredBy: triggeredBy
        ? `${triggeredBy.tagName?.toLowerCase?.() || 'element'}${triggeredBy.id ? `#${triggeredBy.id}` : ''}${triggeredBy.className ? `.${String(triggeredBy.className).trim().replace(/\s+/g, '.')}` : ''}`
        : undefined,
      stack: new Error().stack,
    });
    if (this.classList.contains('active')) {
      this.debug('cart-drawer.open.skip-already-active');
      return;
    }
    this.closeMobileNavigation();
    this.hideMobileNavigationOverlays();
    this.restoreDrawerHeader();
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.restoreDrawerHeader();
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty') || document.getElementById('CartDrawer') || this.querySelector('.drawer__inner')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (!containerToTrapFocusOn || !focusElement) return;
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    this.restoreMobileNavigationOverlays();
  }

  closeMobileNavigation() {
    document
      .querySelectorAll('header-drawer details[open], menu-drawer details[open], .menu-drawer-container[open]')
      .forEach((details) => {
        details.removeAttribute('open');
        details.classList.remove('menu-opening');
        details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
        details.querySelectorAll('details[open]').forEach((childDetails) => {
          childDetails.removeAttribute('open');
          childDetails.classList.remove('menu-opening');
          childDetails.querySelector('summary')?.setAttribute('aria-expanded', 'false');
        });
      });

    document.querySelectorAll('.submenu-open').forEach((element) => element.classList.remove('submenu-open'));
    document.querySelectorAll('.section-header.menu-open').forEach((element) => element.classList.remove('menu-open'));
    document.body.classList.remove('overflow-hidden-tablet', 'overflow-hidden-desktop');
    removeTrapFocus();

    document.documentElement.dispatchEvent(new CustomEvent('theme:mobile-navigation:close'));
  }

  hideMobileNavigationOverlays() {
    document
      .querySelectorAll('.Meteor-MobileNavigation, #meteor-mobile-nav')
      .forEach((element) => {
        if (element.dataset.cartDrawerPreviousDisplay === undefined) {
          element.dataset.cartDrawerPreviousDisplay = element.style.display || '';
        }
        if (element.dataset.cartDrawerPreviousVisibility === undefined) {
          element.dataset.cartDrawerPreviousVisibility = element.style.visibility || '';
        }
        if (element.dataset.cartDrawerPreviousPointerEvents === undefined) {
          element.dataset.cartDrawerPreviousPointerEvents = element.style.pointerEvents || '';
        }

        element.style.setProperty('display', 'none', 'important');
        element.style.setProperty('visibility', 'hidden', 'important');
        element.style.setProperty('pointer-events', 'none', 'important');
      });
  }

  restoreMobileNavigationOverlays() {
    document
      .querySelectorAll('[data-cart-drawer-previous-display], [data-cart-drawer-previous-visibility], [data-cart-drawer-previous-pointer-events]')
      .forEach((element) => {
        element.style.display = element.dataset.cartDrawerPreviousDisplay || '';
        element.style.visibility = element.dataset.cartDrawerPreviousVisibility || '';
        element.style.pointerEvents = element.dataset.cartDrawerPreviousPointerEvents || '';
        delete element.dataset.cartDrawerPreviousDisplay;
        delete element.dataset.cartDrawerPreviousVisibility;
        delete element.dataset.cartDrawerPreviousPointerEvents;
      });
  }

  observeDrawerHeader() {
    this.drawerHeaderObserver = new MutationObserver(() => this.restoreDrawerHeader());
    this.drawerHeaderObserver.observe(this, { childList: true, subtree: true });
  }

  restoreDrawerHeader() {
    const header = this.querySelector('.drawer__header');
    if (!header) return;

    const hasHeading = Boolean(header.querySelector('.drawer__heading'));
    const hasClose = Boolean(header.querySelector('.drawer__close'));
    const hasMeteorNav = Boolean(header.querySelector('.Meteor-MobileNavigation, #meteor-mobile-nav'));
    if (hasHeading && hasClose && !hasMeteorNav) return;

    const itemCount = this.getCartItemCount();
    const headingText = itemCount > 0 ? `CART ${itemCount}` : 'CART';

    header.removeAttribute('data-meteor-mounted');
    header.removeAttribute('data-meteor-platform');
    header.innerHTML = `
      <h2 class="drawer__heading">${headingText}</h2>
      <button class="drawer__close" type="button" aria-label="Close">
        <span class="svg-wrapper">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-close" viewBox="0 0 18 17" aria-hidden="true" focusable="false">
            <path fill="currentColor" d="M.865 15.978a.5.5 0 0 0 .707.707l7.433-7.431 7.579 7.282a.501.501 0 0 0 .846-.37.5.5 0 0 0-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 1 0-.707-.708L8.991 7.853 1.413.573a.5.5 0 1 0-.693.72l7.563 7.268-7.418 7.417Z"></path>
          </svg>
        </span>
      </button>
    `;
    header.querySelector('.drawer__close')?.addEventListener('click', () => this.close());
  }

  getCartItemCount() {
    const bubbleCount =
      Number(document.querySelector('#cart-icon-bubble .cart-count [aria-hidden="true"]')?.textContent || 0) || 0;
    if (bubbleCount > 0) return bubbleCount;

    return Array.from(this.querySelectorAll('#CartDrawer-CartItems .quantity__input')).reduce((count, input) => {
      const quantity = Number(input.value || input.getAttribute('value') || 0);
      return count + (Number.isFinite(quantity) ? quantity : 0);
    }, 0);
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerIsActive = this.classList.contains('active');
    const recentTrustedRenderIntent =
      Date.now() - Number(window.__pupfordLastTrustedCartRenderIntent || 0) < 30000;
    const hasRenderableSections =
      parsedState &&
      parsedState.sections &&
      typeof parsedState.sections === 'object';
    const shouldOpenAfterRender = hasRenderableSections && !Array.isArray(parsedState.items);

    this.debug('cart-drawer.renderContents', {
      drawerIsActive,
      recentTrustedRenderIntent,
      shouldOpenAfterRender,
      payload: this.summarizeCartPayload(parsedState),
      stack: new Error().stack,
    });

    if (!drawerIsActive && !recentTrustedRenderIntent) {
      this.debug('cart-drawer.renderContents.skip-inactive-no-intent');
      return;
    }

    if (!hasRenderableSections) {
      this.debug('cart-drawer.renderContents.dispatch-refresh-event', {
        payload: this.summarizeCartPayload(parsedState),
      });
      document.dispatchEvent(
        new CustomEvent('pupford:cart-drawer-rendered', {
          detail: { cart: parsedState, source: 'cart-drawer-refresh' },
        })
      );
      return;
    }

    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      this.debug('cart-drawer.renderContents.replace-section', {
        sectionId: section.id,
        selector: section.selector,
      });
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    document.dispatchEvent(
      new CustomEvent('pupford:cart-drawer-rendered', {
        detail: { cart: parsedState, source: 'cart-drawer' },
      })
    );

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
      if (this.classList.contains('active')) {
        this.debug('cart-drawer.renderContents.skip-open-already-active');
        return;
      }
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: 'cart-drawer-items',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
